/**
 * V1.9.6 — Opening Overlay Layer
 *
 * Extracted from FloorPlan2D.jsx to keep that file focused on the 2D
 * canvas. This layer is purely presentational: invisible (alpha 0.001)
 * rects sitting on top of every door + window so users can drag-along-
 * wall and click-to-edit them. It also paints the cyan dashed
 * "axis hint" line while a drag is in flight.
 *
 * All persistence + materialisation lives in the parent — this
 * component only forwards pointer events back up via the callbacks.
 *
 * Props:
 *   doorPairs:        [{ axis, coord, span_lo, span_hi, position, _explicitIdx, ... }]
 *   exteriorWindows:  [{ axis, coord, centre, len, _explicitIdx, ... }]
 *   openingDrag:      { kind, idx, axis, coord, span_lo, span_hi, halfLen_m } | null
 *   toX(worldX):      number — convert world metres to screen px (X)
 *   toY(worldZ):      number — convert world metres to screen px (Y, flipped)
 *   scale:            number — px per metre at current zoom
 *   onOverlayPointerDown(e, kind, source)
 *   onOverlayClick(kind, e, source)
 */
import React from "react";

export default function OpeningOverlayLayer({
  doorPairs,
  exteriorWindows,
  openingDrag,
  toX,
  toY,
  scale,
  onOverlayPointerDown,
  onOverlayPointerUp,
  onOverlayClick,
}) {
  return (
    <>
      {/* V1.9.4 — axis-hint dashed line. */}
      {openingDrag && (() => {
        const lo = openingDrag.span_lo + openingDrag.halfLen_m;
        const hi = openingDrag.span_hi - openingDrag.halfLen_m;
        const isX = openingDrag.axis === "x";
        const x1 = isX ? toX(lo) : toX(openingDrag.coord);
        const x2 = isX ? toX(hi) : toX(openingDrag.coord);
        const y1 = isX ? toY(openingDrag.coord) : toY(lo);
        const y2 = isX ? toY(openingDrag.coord) : toY(hi);
        return (
          <line
            data-testid="opening-drag-axis-hint"
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="#c4a96b"
            strokeWidth="1.5"
            strokeDasharray="4 4"
            strokeLinecap="round"
            opacity="0.85"
            pointerEvents="none"
          />
        );
      })()}

      {/* Door overlays. */}
      {doorPairs.map((d, i) => {
        // V2.0.0 — Both the React key and the data-testid are derived
        // from the door's STABLE id (assigned in openings.js based on
        // room identity). When an auto-inferred door is materialised
        // into an explicit row mid-interaction, the id is preserved, so
        // React keeps the same DOM node and the click event fires
        // reliably. Previously the key flipped between `auto-${i}` and
        // `e${explicitIdx}`, unmounting the rect before click and
        // making AI-door deletion impossible.
        const stableId = d.id || `idx-${i}`;
        const len = d.span_hi - d.span_lo;
        const pos = Number.isFinite(d.position) ? d.position : 0.5;
        const cWorld = d.span_lo + pos * len;
        let x, y, w, h;
        if (d.axis === "x") {
          const px = toX(cWorld);
          const py = toY(d.coord);
          const lenPx = Math.min(len, 1.4) * scale;
          w = lenPx; h = 14; x = px - w / 2; y = py - h / 2;
        } else {
          const px = toX(d.coord);
          const py = toY(cWorld);
          const lenPx = Math.min(len, 1.4) * scale;
          w = 14; h = lenPx; x = px - w / 2; y = py - h / 2;
        }
        return (
          <rect
            key={`door-overlay-${stableId}`}
            data-testid={`floor-plan-door-overlay-${stableId}`}
            x={x} y={y} width={w} height={h}
            fill="rgba(0,0,0,0.001)"
            style={{ cursor: openingDrag?.idx === d._explicitIdx && d._explicitIdx >= 0 ? "grabbing" : "grab" }}
            onPointerDown={(e) => onOverlayPointerDown(e, "door", d)}
            onClick={(e) => onOverlayClick("door", e, d)}
          />
        );
      })}

      {/* Window overlays. */}
      {exteriorWindows.map((w, i) => {
        // V2.0.0 — Stable key/testid via w.id (auto windows now carry an
        // identity-based id "auto-W-<room>-<side>-<centre>"). Fixes the
        // "delete deletes a different window than the one I clicked" bug
        // that was caused by index-based keys rebinding click handlers
        // to the wrong source after materialise.
        const stableId = w.id || `idx-${i}`;
        let x, y, rw, rh;
        if (w.axis === "x") {
          const px = toX(w.centre);
          const py = toY(w.coord);
          const lenPx = w.len * scale;
          rw = lenPx; rh = 14; x = px - rw / 2; y = py - rh / 2;
        } else {
          const px = toX(w.coord);
          const py = toY(w.centre);
          const lenPx = w.len * scale;
          rw = 14; rh = lenPx; x = px - rw / 2; y = py - rh / 2;
        }
        return (
          <rect
            key={`window-overlay-${stableId}`}
            data-testid={`floor-plan-window-overlay-${stableId}`}
            x={x} y={y} width={rw} height={rh}
            fill="rgba(0,0,0,0.001)"
            style={{ cursor: openingDrag?.idx === w._explicitIdx && w._explicitIdx >= 0 ? "grabbing" : "grab" }}
            onPointerDown={(e) => onOverlayPointerDown(e, "window", w)}
            onClick={(e) => onOverlayClick("window", e, w)}
          />
        );
      })}
    </>
  );
}
